import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api, { trainingNodesAPI, sopsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import SOPQuizModal from '../components/SOPQuizModal';
import { 
  ClipboardCheck, 
  CheckCircle,
  Circle,
  ExternalLink,
  Calendar,
  GraduationCap,
  BookOpen,
  FileQuestion,
  ChevronRight,
  Play,
  Lock,
  Sparkles,
  Trophy,
  ArrowRight,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const OnboardingWorkflow = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  
  // Onboarding checklist data
  const [checklistItems, setChecklistItems] = useState([]);
  const [checklistCompletions, setChecklistCompletions] = useState([]);
  
  // Training data
  const [assignedTrainings, setAssignedTrainings] = useState([]);
  const [trainingCompletions, setTrainingCompletions] = useState([]);
  
  // SOP data
  const [assignedSOPs, setAssignedSOPs] = useState([]);
  const [sopCompletions, setSOPCompletions] = useState([]);
  
  // Quiz state
  const [quizStatuses, setQuizStatuses] = useState({});
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [selectedSOPForQuiz, setSelectedSOPForQuiz] = useState(null);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      const [
        checklistRes,
        checklistCompRes,
        assignedRes,
        sopsRes
      ] = await Promise.all([
        api.get('/onboarding-checklist'),
        api.get('/my-onboarding-completions'),
        trainingNodesAPI.getMyAssigned().catch(() => ({ data: { trainings: [], sops: [] } })),
        api.get('/my-sop-completions').catch(() => ({ data: [] }))
      ]);
      
      setChecklistItems(checklistRes.data || []);
      setChecklistCompletions(checklistCompRes.data || []);
      setAssignedTrainings(assignedRes.data?.trainings || []);
      const sops = assignedRes.data?.sops || [];
      setAssignedSOPs(sops);
      setTrainingCompletions(assignedRes.data?.trainings?.filter(t => t.is_completed) || []);
      setSOPCompletions(sopsRes.data || []);
      
      // Load quiz statuses for SOPs with quizzes
      const sopsWithQuizzes = sops.filter(s => s.quiz_id);
      const quizStatusPromises = sopsWithQuizzes.map(async (sop) => {
        try {
          const quizRes = await sopsAPI.getQuiz(sop.id);
          return { sopId: sop.id, passed: quizRes.data.passed, hasQuiz: !!quizRes.data.quiz };
        } catch (e) {
          return { sopId: sop.id, passed: false, hasQuiz: false };
        }
      });
      
      const statuses = await Promise.all(quizStatusPromises);
      const statusMap = {};
      statuses.forEach(s => {
        statusMap[s.sopId] = { passed: s.passed, hasQuiz: s.hasQuiz };
      });
      setQuizStatuses(statusMap);
      
      // Determine active step based on progress
      const checklistComplete = checklistRes.data?.length > 0 && 
        checklistCompRes.data?.length >= checklistRes.data?.length * 0.8;
      const trainingsComplete = assignedRes.data?.trainings?.length > 0 &&
        assignedRes.data?.trainings?.filter(t => t.is_completed).length >= assignedRes.data?.trainings?.length * 0.8;
      
      if (trainingsComplete) {
        setActiveStep(2); // SOPs
      } else if (checklistComplete) {
        setActiveStep(1); // Trainings
      } else {
        setActiveStep(0); // Checklist
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChecklist = async (itemId) => {
    const isCompleted = checklistCompletions.some(c => c.item_id === itemId);
    try {
      if (isCompleted) {
        await api.delete(`/onboarding-checklist/${itemId}/complete`);
        setChecklistCompletions(prev => prev.filter(c => c.item_id !== itemId));
      } else {
        await api.post(`/onboarding-checklist/${itemId}/complete`);
        setChecklistCompletions(prev => [...prev, { item_id: itemId, completed_at: new Date().toISOString() }]);
        toast.success('Task completed!');
      }
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleMarkTrainingComplete = async (nodeId) => {
    try {
      await trainingNodesAPI.markComplete(nodeId);
      setAssignedTrainings(prev => prev.map(t => 
        t.id === nodeId ? { ...t, is_completed: true } : t
      ));
      toast.success('Training completed!');
    } catch (error) {
      toast.error('Failed to mark complete');
    }
  };

  const handleMarkSOPComplete = async (sopId) => {
    try {
      await api.post(`/sops/${sopId}/complete`);
      setSOPCompletions(prev => [...prev, { sop_id: sopId }]);
      toast.success('SOP marked as read!');
    } catch (error) {
      // Check if quiz is required
      if (error.response?.data?.detail?.includes('quiz')) {
        toast.error('You must pass the quiz before completing this SOP');
        // Find the SOP and open quiz
        const sop = assignedSOPs.find(s => s.id === sopId);
        if (sop) {
          handleOpenQuiz(sop);
        }
      } else {
        toast.error('Failed to mark SOP');
      }
    }
  };

  // Quiz helpers
  const hasQuiz = (sop) => {
    return !!sop.quiz_id || quizStatuses[sop.id]?.hasQuiz;
  };

  const quizPassed = (sop) => {
    return quizStatuses[sop.id]?.passed || false;
  };

  const handleOpenQuiz = (sop) => {
    setSelectedSOPForQuiz(sop);
    setShowQuizModal(true);
  };

  const handleQuizPassed = (sopId) => {
    // Update quiz status
    setQuizStatuses(prev => ({
      ...prev,
      [sopId]: { ...prev[sopId], passed: true }
    }));
    // Also mark as complete
    setSOPCompletions(prev => [...prev, { sop_id: sopId }]);
    setShowQuizModal(false);
    toast.success('SOP completed!');
  };

  // Check if SOP can be worked on (sequential logic)
  const canWorkOnSOP = (sopIndex) => {
    // First SOP is always accessible
    if (sopIndex === 0) return true;
    
    // Check if previous SOP is completed
    const previousSop = assignedSOPs[sopIndex - 1];
    if (!previousSop) return true;
    
    const isPreviousCompleted = sopCompletions.some(c => c.sop_id === previousSop.id);
    return isPreviousCompleted;
  };

  // Calculate progress for each section
  const checklistProgress = checklistItems.length > 0 
    ? Math.round((checklistCompletions.length / checklistItems.length) * 100) 
    : 0;
  
  const trainingProgress = assignedTrainings.length > 0 
    ? Math.round((assignedTrainings.filter(t => t.is_completed).length / assignedTrainings.length) * 100) 
    : 0;
  
  const sopProgress = assignedSOPs.length > 0 
    ? Math.round((sopCompletions.length / assignedSOPs.length) * 100) 
    : 0;

  const overallProgress = Math.round((checklistProgress + trainingProgress + sopProgress) / 3);

  // Group checklist by section
  const checklistBySection = checklistItems.reduce((acc, item) => {
    const section = item.section || 'General';
    if (!acc[section]) acc[section] = [];
    acc[section].push(item);
    return acc;
  }, {});

  // Group trainings by category
  const trainingsByCategory = assignedTrainings.reduce((acc, t) => {
    const cat = t.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  const workflowSteps = [
    { 
      id: 0, 
      title: 'Setup & Checklist', 
      icon: ClipboardCheck, 
      progress: checklistProgress,
      total: checklistItems.length,
      completed: checklistCompletions.length,
      description: 'Complete your onboarding tasks'
    },
    { 
      id: 1, 
      title: 'Training Videos', 
      icon: GraduationCap, 
      progress: trainingProgress,
      total: assignedTrainings.length,
      completed: assignedTrainings.filter(t => t.is_completed).length,
      description: 'Watch role-specific training videos'
    },
    { 
      id: 2, 
      title: 'SOP Training', 
      icon: BookOpen, 
      progress: sopProgress,
      total: assignedSOPs.length,
      completed: sopCompletions.length,
      description: 'Read and acknowledge SOPs'
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="onboarding-workflow">
      {/* Header with Overall Progress */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Sparkles className="w-6 h-6" />
              Welcome to Your Onboarding Journey
            </h1>
            <p className="text-blue-100 mt-1">
              Hi {user?.name}! Complete these steps to get fully onboarded.
            </p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold">{overallProgress}%</div>
            <p className="text-blue-200 text-sm">Overall Progress</p>
          </div>
        </div>
        <div className="mt-4">
          <Progress value={overallProgress} className="h-3 bg-indigo-400" />
        </div>
        {overallProgress === 100 && (
          <div className="mt-4 flex items-center gap-2 bg-white/20 rounded-lg p-3">
            <Trophy className="w-6 h-6 text-yellow-300" />
            <span className="font-medium">Congratulations! You've completed your onboarding!</span>
          </div>
        )}
      </div>

      {/* Workflow Steps Navigation */}
      <div className="flex items-center justify-between bg-white rounded-xl p-4 shadow-card border border-slate-200">
        {workflowSteps.map((step, index) => (
          <React.Fragment key={step.id}>
            <button
              onClick={() => setActiveStep(step.id)}
              className={`flex-1 flex items-center gap-3 p-3 rounded-lg transition-all ${
                activeStep === step.id 
                  ? 'bg-blue-50 border-2 border-blue-500' 
                  : 'hover:bg-slate-50 border-2 border-transparent'
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                step.progress === 100 
                  ? 'bg-emerald-100 text-emerald-600' 
                  : activeStep === step.id 
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-slate-100 text-slate-500'
              }`}>
                {step.progress === 100 ? (
                  <CheckCircle className="w-6 h-6" />
                ) : (
                  <step.icon className="w-6 h-6" />
                )}
              </div>
              <div className="text-left">
                <p className={`font-medium ${activeStep === step.id ? 'text-blue-700' : 'text-slate-700'}`}>
                  {step.title}
                </p>
                <p className="text-xs text-slate-500">
                  {step.completed}/{step.total} completed
                </p>
              </div>
              <div className="ml-auto">
                <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${
                      step.progress === 100 ? 'bg-emerald-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${step.progress}%` }}
                  />
                </div>
                <p className="text-xs text-slate-500 text-right mt-1">{step.progress}%</p>
              </div>
            </button>
            {index < workflowSteps.length - 1 && (
              <ChevronRight className="w-5 h-5 text-slate-300 mx-2 flex-shrink-0" />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <div className="min-h-[400px]">
        {/* Step 0: Checklist */}
        {activeStep === 0 && (
          <div className="space-y-4">
            <Card className="border-slate-200 shadow-card">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5 text-blue-600" />
                  Onboarding Checklist
                  <Badge className="ml-2 bg-blue-100 text-blue-700">
                    {checklistCompletions.length}/{checklistItems.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {Object.entries(checklistBySection).map(([section, items]) => (
                  <div key={section} className="border-b border-slate-100 last:border-0">
                    <div className="px-4 py-3 bg-slate-50 flex items-center justify-between">
                      <span className="font-medium text-slate-700">{section}</span>
                      <Badge variant="outline">
                        {items.filter(i => checklistCompletions.some(c => c.item_id === i.id)).length}/{items.length}
                      </Badge>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {items.map(item => {
                        const isCompleted = checklistCompletions.some(c => c.item_id === item.id);
                        return (
                          <div 
                            key={item.id} 
                            className={`flex items-start gap-3 p-4 ${isCompleted ? 'bg-emerald-50/50' : ''}`}
                          >
                            <Checkbox 
                              checked={isCompleted}
                              onCheckedChange={() => handleToggleChecklist(item.id)}
                              className="mt-0.5"
                            />
                            <div className="flex-1">
                              <p className={`font-medium ${isCompleted ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                                {item.title}
                              </p>
                              {item.description && (
                                <p className="text-sm text-slate-500 mt-0.5">{item.description}</p>
                              )}
                              {item.link && (
                                <a 
                                  href={item.link} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mt-1"
                                >
                                  <ExternalLink className="w-3 h-3" /> Open Link
                                </a>
                              )}
                            </div>
                            {isCompleted && <CheckCircle className="w-5 h-5 text-emerald-500" />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
            
            {checklistProgress >= 80 && (
              <div className="flex justify-end">
                <Button onClick={() => setActiveStep(1)} className="bg-blue-600 hover:bg-blue-700">
                  Continue to Training Videos <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 1: Training Videos */}
        {activeStep === 1 && (
          <div className="space-y-4">
            <Card className="border-slate-200 shadow-card">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-blue-600" />
                  Training Videos
                  <Badge className="ml-2 bg-blue-100 text-blue-700">
                    {assignedTrainings.filter(t => t.is_completed).length}/{assignedTrainings.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {assignedTrainings.length === 0 ? (
                  <div className="text-center py-12">
                    <GraduationCap className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">No trainings assigned yet</p>
                  </div>
                ) : (
                  Object.entries(trainingsByCategory).map(([category, trainings]) => (
                    <div key={category} className="border-b border-slate-100 last:border-0">
                      <div className="px-4 py-3 bg-slate-50 flex items-center justify-between">
                        <span className="font-medium text-slate-700">{category}</span>
                        <Badge variant="outline">
                          {trainings.filter(t => t.is_completed).length}/{trainings.length}
                        </Badge>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {trainings.map(training => (
                          <div 
                            key={training.id} 
                            className={`flex items-center gap-4 p-4 ${training.is_completed ? 'bg-emerald-50/50' : ''}`}
                          >
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              training.is_completed ? 'bg-emerald-100' : 'bg-blue-100'
                            }`}>
                              {training.is_completed ? (
                                <CheckCircle className="w-5 h-5 text-emerald-600" />
                              ) : (
                                <Play className="w-5 h-5 text-blue-600" />
                              )}
                            </div>
                            <div className="flex-1">
                              <p className={`font-medium ${training.is_completed ? 'text-slate-500' : 'text-slate-900'}`}>
                                {training.label}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                {training.role_requirements?.[user?.job_role] === 'M' && (
                                  <Badge className="bg-red-100 text-red-700 text-xs">Mandatory</Badge>
                                )}
                                {training.role_requirements?.[user?.job_role] === 'R' && (
                                  <Badge variant="outline" className="text-xs">Reference</Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {training.link && (
                                <a href={training.link} target="_blank" rel="noopener noreferrer">
                                  <Button variant="outline" size="sm">
                                    <Play className="w-4 h-4 mr-1" /> Watch
                                  </Button>
                                </a>
                              )}
                              {!training.is_completed && (
                                <Button 
                                  size="sm" 
                                  onClick={() => handleMarkTrainingComplete(training.id)}
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" /> Done
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
            
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setActiveStep(0)}>
                Back to Checklist
              </Button>
              {trainingProgress >= 80 && (
                <Button onClick={() => setActiveStep(2)} className="bg-blue-600 hover:bg-blue-700">
                  Continue to SOP Training <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Step 2: SOP Training */}
        {activeStep === 2 && (
          <div className="space-y-4">
            <Card className="border-slate-200 shadow-card">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                  SOP Training
                  <Badge className="ml-2 bg-blue-100 text-blue-700">
                    {sopCompletions.length}/{assignedSOPs.length}
                  </Badge>
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Complete each SOP and pass the quiz before moving to the next one
                </p>
              </CardHeader>
              <CardContent className="p-0">
                {assignedSOPs.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">No SOPs assigned yet</p>
                    <Link to="/sops">
                      <Button variant="outline" className="mt-3">
                        Go to SOP Library
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {assignedSOPs.map((sop, index) => {
                      const isCompleted = sopCompletions.some(c => c.sop_id === sop.id);
                      const sopHasQuiz = hasQuiz(sop);
                      const sopQuizPassed = quizPassed(sop);
                      // Only lock if not completed AND previous not completed
                      const isLocked = !isCompleted && !canWorkOnSOP(index);
                      
                      return (
                        <div 
                          key={sop.id} 
                          className={`flex items-center gap-4 p-4 transition-colors ${
                            isCompleted ? 'bg-emerald-50/50' : 
                            isLocked ? 'bg-slate-50/50 opacity-60' : ''
                          }`}
                          data-testid={`onboarding-sop-${sop.id}`}
                        >
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            isCompleted ? 'bg-emerald-100' : 
                            isLocked ? 'bg-slate-200' : 'bg-slate-100'
                          }`}>
                            {isCompleted ? (
                              <CheckCircle className="w-5 h-5 text-emerald-600" />
                            ) : isLocked ? (
                              <Lock className="w-5 h-5 text-slate-400" />
                            ) : (
                              <BookOpen className="w-5 h-5 text-slate-500" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-slate-400 font-mono">#{index + 1}</span>
                              <p className={`font-medium ${isCompleted ? 'text-slate-500' : isLocked ? 'text-slate-400' : 'text-slate-900'}`}>
                                {sop.title}
                              </p>
                              {sopHasQuiz && (
                                <Badge className={`text-xs ${
                                  sopQuizPassed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                                }`}>
                                  <FileQuestion className="w-3 h-3 mr-1" />
                                  {sopQuizPassed ? 'Quiz Passed' : 'Quiz Required'}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500">{sop.category} • {sop.version}</p>
                            {isLocked && (
                              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                Complete the previous SOP first
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {sop.document_url && !isLocked && (
                              <a href={sop.document_url} target="_blank" rel="noopener noreferrer">
                                <Button variant="outline" size="sm">
                                  <ExternalLink className="w-4 h-4 mr-1" /> Read
                                </Button>
                              </a>
                            )}
                            {!isCompleted && !isLocked && (
                              <>
                                {sopHasQuiz && !sopQuizPassed ? (
                                  <Button 
                                    size="sm" 
                                    onClick={() => handleOpenQuiz(sop)}
                                    className="bg-blue-600 hover:bg-blue-700"
                                    data-testid={`take-quiz-btn-${sop.id}`}
                                  >
                                    <FileQuestion className="w-4 h-4 mr-1" /> Take Quiz
                                  </Button>
                                ) : (
                                  <Button 
                                    size="sm" 
                                    onClick={() => handleMarkSOPComplete(sop.id)}
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" /> Complete
                                  </Button>
                                )}
                              </>
                            )}
                            {isCompleted && (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                <CheckCircle className="w-3 h-3 mr-1" /> Done
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
            
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setActiveStep(1)}>
                Back to Trainings
              </Button>
              {sopProgress === 100 && (
                <Link to="/reports">
                  <Button className="bg-emerald-600 hover:bg-emerald-700">
                    <Trophy className="w-4 h-4 mr-2" /> View Training Report
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quick Links */}
      <Card className="border-slate-200 shadow-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">Need to access specific sections?</p>
            <div className="flex gap-2">
              <Link to="/trainings">
                <Button variant="outline" size="sm">
                  <GraduationCap className="w-4 h-4 mr-1" /> All Trainings
                </Button>
              </Link>
              <Link to="/sops">
                <Button variant="outline" size="sm">
                  <BookOpen className="w-4 h-4 mr-1" /> SOP Library
                </Button>
              </Link>
              <Link to="/reports">
                <Button variant="outline" size="sm">
                  <FileQuestion className="w-4 h-4 mr-1" /> My Reports
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SOP Quiz Modal */}
      <SOPQuizModal
        isOpen={showQuizModal}
        onClose={() => setShowQuizModal(false)}
        sop={selectedSOPForQuiz}
        onQuizPassed={handleQuizPassed}
      />
    </div>
  );
};

export default OnboardingWorkflow;
